import {
  addDays,
  endOfDay,
  formatISO,
  isSameDay,
  startOfDay,
  subMonths,
  subYears,
} from 'date-fns';

import type { FilterValue } from 'src/components/app/context/FilterContext';
import type { DateRange } from 'src/components/ui/DateRangePicker';

export type RequiredDateRange = Required<DateRange>;
export type DateRangeAsStrings = Required<{
  startDate: string;
  endDate: string;
}>;

const today = new Date();
const tomorrow = addDays(today, 1);

export const presetDateRanges = [
  {
    label: 'month',
    startDate: subMonths(tomorrow, 1),
    endDate: today,
  },
  {
    label: 'quarter',
    startDate: subMonths(tomorrow, 3),
    endDate: today,
  },
  {
    label: 'year',
    startDate: subYears(tomorrow, 1),
    endDate: today,
  },
];

export const defaultDateRange: Readonly<RequiredDateRange> =
  presetDateRanges[2];

export const dateRangeToFilter = (
  dateRange: RequiredDateRange,
  filterKey = 'dateRange',
): FilterValue<DateRangeAsStrings> => ({
  label: filterKey,
  filterExpr: {
    startDate: dateRange.startDate.toString(),
    endDate: dateRange.endDate.toString(),
  },
});

export const filterToDateRange = (
  filter: FilterValue<DateRangeAsStrings>,
): RequiredDateRange => {
  const { startDate, endDate } = filter.filterExpr;

  return {
    startDate: new Date(startDate),
    endDate: new Date(endDate),
  };
};

export type DateRangeApiString = {
  createdAtFrom: string;
  createdAtTo: string;
};

export const dateRangeFilterToApiString = (
  filter: FilterValue<DateRangeAsStrings>,
) => {
  const { startDate, endDate } = filterToDateRange(filter);
  return {
    createdAtFrom: formatISO(startOfDay(new Date(startDate))),
    createdAtTo: formatISO(endOfDay(new Date(endDate))),
  };
};

export const areDateRangesEqual = (
  dateRange1: RequiredDateRange,
  dateRange2: RequiredDateRange,
) =>
  isSameDay(dateRange1.startDate, dateRange2.startDate) &&
  isSameDay(dateRange1.endDate, dateRange2.endDate);
